import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Plus, Search } from 'lucide-react';
import { useAllAgents, type Agent } from '../hooks/useAgents';

const roleColor: Record<string, string> = {
  creator: 'bg-purple-100 text-purple-700',
  executor: 'bg-blue-100 text-blue-700',
  researcher: 'bg-amber-100 text-amber-700',
};

function AgentCard({ agent, onClick }: { agent: Agent; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="rounded-xl border border-slate-200 bg-white p-4 text-left transition-colors hover:border-slate-300"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100">
            <Bot className="h-4 w-4 text-slate-600" />
          </div>
          <span className="text-sm font-medium text-slate-900">{agent.name}</span>
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${roleColor[agent.role] ?? 'bg-slate-100 text-slate-600'}`}
        >
          {agent.role}
        </span>
      </div>
      <p className="mt-2 line-clamp-2 text-xs text-slate-500">
        {agent.systemPrompt?.slice(0, 120) || 'No system prompt'}
        {agent.systemPrompt && agent.systemPrompt.length > 120 ? '...' : ''}
      </p>
      <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-400">
        {agent.defaultModel && <span>{agent.defaultModel}</span>}
        <span className={agent.scope === 'global' ? 'text-emerald-600' : 'text-slate-400'}>
          {agent.scope === 'global' ? 'Global' : 'Project'}
        </span>
      </div>
    </button>
  );
}

export default function GlobalAgents() {
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const { data: agents, isLoading } = useAllAgents();

  const filtered = (agents ?? []).filter((agent) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      agent.name.toLowerCase().includes(q) ||
      agent.role.toLowerCase().includes(q) ||
      agent.systemPrompt?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Agents</h1>
          <p className="mt-1 text-sm text-slate-500">Manage your global agent definitions</p>
        </div>
        <button
          className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
        >
          <Plus className="h-4 w-4" />
          Create Agent
        </button>
      </div>

      {/* Search */}
      <div className="mb-6 relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search agents by name, role, or prompt..."
          className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-400 focus:outline-none"
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="text-sm text-slate-400">Loading agents...</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <Bot className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="text-sm text-slate-500">
            {search ? 'No agents match your search.' : 'No global agents yet. Create one to get started.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              onClick={() => navigate(`/agents/${agent.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
